import { z } from 'zod';

/**
 * Schéma de validation des variables d'environnement.
 * Toutes les vars Supabase doivent pointer vers la NOUVELLE instance autonome.
 */
const envSchema = z.object({
  // --- Supabase autonome (obligatoire) ---
  VITE_SUPABASE_URL: z
    .string()
    .url('VITE_SUPABASE_URL doit être une URL valide')
    .refine((url) => url.includes('supabase.co') || url.includes('supabase.in'), {
      message: 'VITE_SUPABASE_URL doit pointer vers une instance Supabase',
    }),
  VITE_SUPABASE_PUBLISHABLE_KEY: z
    .string()
    .min(30, 'VITE_SUPABASE_PUBLISHABLE_KEY semble invalide (trop court)'),

  // --- Identifiant de la cible de build ---
  VITE_BUILD_TARGET: z
    .enum(['web', 'ios', 'android'])
    .default('web')
    .describe('Cible de build : web | ios | android'),

  // --- Optionnel : Supabase ancienne instance (lecture seule, migration) ---
  VITE_LEGACY_SUPABASE_URL: z.string().url().optional(),
  VITE_LEGACY_SUPABASE_KEY: z.string().optional(),
});

export type Env = z.infer<typeof envSchema>;

// Fallback connu — évite le crash TDZ si les env vars Vercel sont absentes
const FALLBACK_URL = 'https://ximcmpjnzqacydorvecs.supabase.co';
const FALLBACK_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhpbWNtcGpuenFhY3lkb3J2ZWNzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQ2NTM0NjAsImV4cCI6MjA5MDIyOTQ2MH0.zqSdrLaXUvsF1KrhMQMkxU0pcQ6xbK3XdObBZiGMGtQ';

function loadEnv(): Env {
  const raw = {
    VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
    VITE_SUPABASE_PUBLISHABLE_KEY: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    VITE_BUILD_TARGET: import.meta.env.VITE_BUILD_TARGET,
    VITE_LEGACY_SUPABASE_URL: import.meta.env.VITE_LEGACY_SUPABASE_URL,
    VITE_LEGACY_SUPABASE_KEY: import.meta.env.VITE_LEGACY_SUPABASE_KEY,
  };

  const result = envSchema.safeParse(raw);

  if (!result.success) {
    const errors = result.error.flatten().fieldErrors;
    console.warn('⚠️ Env vars invalides, fallback Supabase activé:', errors);
    // NEVER throw here — it leaves `env` in TDZ and crashes the entire app
    return {
      VITE_SUPABASE_URL: (raw.VITE_SUPABASE_URL as string) || FALLBACK_URL,
      VITE_SUPABASE_PUBLISHABLE_KEY: (raw.VITE_SUPABASE_PUBLISHABLE_KEY as string) || FALLBACK_KEY,
      VITE_BUILD_TARGET: 'web',
    } as Env;
  }

  return result.data;
}

/** Configuration validée — singleton */
export const env = loadEnv();

/** Helpers */
export const isNative = () => env.VITE_BUILD_TARGET !== 'web';
export const isIOS = () => env.VITE_BUILD_TARGET === 'ios';
export const isAndroid = () => env.VITE_BUILD_TARGET === 'android';
export const isWeb = () => env.VITE_BUILD_TARGET === 'web';
